import { SimpleGrid, useDisclosure, useToast } from "@chakra-ui/react"
import axios from "axios"
import { chakraComponents } from "chakra-react-select"
import { useFormik } from "formik"
import _, { get } from "lodash"
import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import { printAction } from "../../../../actions"
import { BASE_URL } from "../../../../consts/app.consts"
import { PRINT } from "../../../../consts/methods.consts"
import { REPORT_TAGS } from "../../../../consts/tags.consts"
import { useGetQuery } from "../../../../services/api.service"
import { ToastService } from "../../../../services/toast.service"
import ChakraModal from "../../../common/chakra-modal/ChakraModal"
import ChakraTable from "../../../common/chakra-table/ChakraTable"
import FormOnlineSearch from "../../../common/form-controllers/FormOnlineSearch"
import FormSelect from "../../../common/form-controllers/FormSelect"
import InnerLoading from "../../../common/inner-loading/InnterLoading"
import PageHeader, { PageHeaderActions } from "../../../common/page-header/PageHeader"
import { SectionCard } from "../../../common/section-card/SectionCard"
import { useTableDataProvider } from "../../../hooks"
import { BadgeCell, DefaultCell } from "../components/cells"

const ModuleWiseReviewReport = () => {
    const disclosure = useDisclosure()
    const [loading, setLoading] = useState(false)
    const [filter, setFilter]: any = useState({})
    const toast = new ToastService(useToast());

    const formik = useFormik({
        initialValues: {},
        validationSchema: Yup.object({
            p_coordinator: Yup.string().required('Program coordinator is required'),
            program: Yup.string().required('Program is required'),
            module: Yup.string().required('Module is required')
        }),
        onSubmit: async (values: any) => {
            setFilter(values)
        }
    })

    const { p_coordinator, program, module }: any = formik.values

    const { data: _data, isLoading, isFetching } = useGetQuery({
        method: 'xceed.api.report.module_evaluation.get_module_wise_evaluation_report',
        body: {
            filter: filter
        },
        providesTags: [REPORT_TAGS.LIST]
    })

    const data: any = get(_data, 'message', [])
    const pageLoading = isLoading || isFetching || loading

    const generateReport = async () => {
        if (p_coordinator && program && module) {
            setLoading(true)
            try {
                await printAction(PRINT.PRINT_MODULE_WISE_EVALUATION_REPORT, { filter }, setLoading);
                setLoading(false)

            } catch (error: any) {
                setLoading(false)
                toast.setDescription('Unable to generate report').setTitle('Error').setStatus("error").show()
            }
        }
        else {
            toast.setDescription('please select program coodinator , program and module ').setTitle('Warning').setStatus("warning").show()
        }
    }

    const executeReportHandler = () => {
        formik.submitForm()
        if (!module || !program || !p_coordinator) {
            toast.setDescription('Please select a program coordinator , program and module').setTitle('Warning').setStatus('warning').show()
            return
        }
    }

    const actions: PageHeaderActions = [
        {
            text: "Print Report",
            onClick: generateReport,
            buttonVariant: "solid",
            isDisabled: pageLoading,
            buttonColorScheme: "teal",
            confirmation: false
        },
        {
            text: "Execute Report",
            onClick: executeReportHandler,
            buttonVariant: "solid",
            isDisabled: pageLoading,
            buttonColorScheme: "teal",
            confirmation: false
        },
        {
            text: "Reset",
            onClick: () => formik.resetForm(),
            isDisabled: pageLoading,
            buttonVariant: "solid",
            buttonColorScheme: "red",
            confirmation: false
        }
    ]

    useEffect(() => {
        if (p_coordinator) {
            formik.setFieldValue('program', '')
        }

    }, [p_coordinator])

    useEffect(() => {
        if (program) {
            formik.setFieldValue('module', '')
        }
    }, [program])

    const ReportComponent = () => {
        if (isLoading || isFetching) {
            return <InnerLoading />;
        }

        if (!data || _.isEmpty(data)) {
            return <p className="text-center text-gray-500 mt-4">No data available</p>;
        }

        return (
            <>
                {Object.entries(data).map(([key, value]: any, index) => {
                    if (key === "Assessment of Resource Persons") {
                        return (
                            <SectionWrapper key={`${key}-${index}`} title="Resource Person Evaluation">
                                <ResourcePersonEvaluationTable tableData={value} />
                            </SectionWrapper>
                        );
                    }

                    return (
                        <SectionWrapper key={`${key}-${index}`} title={`${key}${value?.rate ? ` (${value.rate})` : ""}`}>
                            <GeneralTable tableData={value?.fields} />
                        </SectionWrapper>
                    );
                })}
            </>
        );
    };

    return (
        <>
            <div>
                <PageHeader enableBackButton isLoading={isFetching || isLoading }
                    actions={actions}
                    subtitle="View and Manage Report" title={'Program Module Evaluation'} />

                <SectionCard backgroundColor=" bg-white" title={'Filters'} p={3} mb={4}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4, xl: 4 }} spacing={3}>
                        <FormOnlineSearch
                            method={'xceed.api.report.module_evaluation.get_program_coordinators'}
                            formik={formik}
                            body={{}}
                            name={'p_coordinator'}
                            label={'Program Coordinator'}
                            isRequired
                            isDisabled={pageLoading}
                        />
                        <FormOnlineSearch
                            method={'xceed.api.report.module_evaluation.get_related_programs'}
                            formik={formik} body={{
                                p_coordinator: get(formik.values, 'p_coordinator')
                            }}
                            name={'program'}
                            label={'Program'}
                            isRequired
                            isDisabled={pageLoading}
                        />
                        <FormOnlineSearch
                            isRequired
                            method={'xceed.api.report.module_evaluation.get_related_modules'}
                            formik={formik}
                            body={{
                                program: get(formik.values, 'program')
                            }}
                            name={'module'}
                            label={'Module'}
                            isDisabled={pageLoading}
                        />
                    </SimpleGrid>
                </SectionCard>
                <ReportComponent />
            </div>
        </>
    )
}

export default ModuleWiseReviewReport


const GeneralTable = ({ tableData }: any) => {

    const column = [
        {
            header: "Description",
            accessor: "label",
            width: 50
        },
        {
            header: "Overall Marks",

            accessor: "rating",
            headerOptions: {
                className: " justify-end"
            },
            customRenderer: (value: any, item: any) => (
                <div className="flex justify-end">
                    {value}
                </div>
            ),
            width: 200
        },
    ]

    return (
        <ChakraTable size={"sm"}
            data={tableData || []}
            title="Report"
            columns={column} />
    )
}

const ResourcePersonEvaluationTable = ({ tableData }: any) => {

    const column = [
        {
            header: "Resource Person",
            accessor: "resource_person",
            width: 200
        },
        {
            header: "Session",
            accessor: "session",
            width: 300
        },
        {
            header: "Date",
            accessor: "date",
            width: 100
        },
        {
            header: "Start Time",
            accessor: "start_time",
            width: 100
        },
        {
            header: "End Time",
            accessor: "end_time",
            width: 100
        },
        {
            header: "Overall Marks",
            accessor: "overall_rating",
            width: 10,
            headerOptions: {
                className: " !justify-end"
            },
            customRenderer: (value: any, item: any) => (
                <div className="flex justify-end">
                    {value}
                </div>
            ),
        },
    ]

    return (
        <ChakraTable size={"sm"}
            data={tableData || []}
            title="Report"
            columns={column} />
    )

}

const SectionWrapper: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="border rounded mt-4 bg-slate-50 p-3 transition-all">
        <div className="mb-2 flex justify-between font-bold">
            <p>{title}</p>
        </div>
        {children}
    </div>
);